import { takeLatest, call, put, select } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { api } from "../services/api";

export const SET_LOADING = "saga/SET_LOADING";
export const SET_SELECTED = "saga/SET_SELECTED";
export const SET_SELECTED_USERS = "saga/SET_SELECTED_USERS";
export const SET_SELECTED_PERMISSIONS = "saga/SET_SELECTED_PERMISSIONS";
export const UPDATE_USERS = "saga/UPDATE_USERS";
export const SET_UPDATES_LOADING = "saga/SET_UPDATES_LOADING";

export const FETCH_ROLES_SUCCESS = "saga/FETCH_ROLE_SUCCESS";
export const FETCH_PERMISSIONS_SUCCESS = "saga/FETCH_PERMISSIONS_SUCCESS";

export const ADD_ROLE = "saga/ADD_ROLE";
export const ADD_ROLE_SUCCESS = "saga/ADD_ROLE_SUCCESS";

export const EDIT_ROLE = "saga/EDIT_ROLE";
export const EDIT_ROLE_SUCCESS = "saga/EDIT_ROLE_SUCCESS";

export const COPY_ROLE = "saga/COPY_ROLE";
export const COPY_ROLE_SUCCESS = "saga/COPY_ROLE_SUCCESS";

export const DELETE_ROLE = "saga/DELETE_ROLE";
export const DELETE_ROLE_SUCCESS = "saga/DELETE_ROLE_SUCCESS";

export const FETCH_CURRENT_USER = "saga/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/FETCH_CURRENT_USER_SUCCESS";

export const FETCH_USERS_PER_ROLE = "saga/FETCH_USERS_PER_ROLE";
export const FETCH_USERS_PER_ROLE_SUCCESS = "saga/FETCH_USERS_PER_ROLE_SUCCESS";

export const RESET_FILTERS = "saga/RESET_FILTERS";
export const SET_ERROR = "saga/SET_ERROR";

const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  createRole: createAction(ADD_ROLE),
  changeRole: createAction(EDIT_ROLE),
  deleteRole: createAction(DELETE_ROLE),
  setSelected: createAction(SET_SELECTED),
  resetFilters: createAction(RESET_FILTERS),
  fetchUsersPerRole: createAction(FETCH_USERS_PER_ROLE),
  setSelectedUsers: createAction(SET_SELECTED_USERS),
  setSelectedPermissions: createAction(SET_SELECTED_PERMISSIONS),
  updateUsers: createAction(UPDATE_USERS),
  copyRole: createAction(COPY_ROLE)
};

export const {
  fetchCurrentUser,
  createRole,
  changeRole,
  deleteRole,
  setSelected,
  updateFilters,
  resetFilters,
  fetchUsersPerRole,
  setSelectedUsers,
  setSelectedPermissions,
  updateUsers,
  copyRole
} = actionCreators;

export default function* watchROLE() {
  yield takeLatestSafe(FETCH_CURRENT_USER, getCurrentUserData);
  yield takeLatestSafe(ADD_ROLE, addRole);
  yield takeLatestSafe(FETCH_USERS_PER_ROLE, getUsersPerRole);
  yield takeLatestSafe(EDIT_ROLE, editRole);
  yield takeLatestSafe(DELETE_ROLE, removeRole);
  yield takeLatestSafe(UPDATE_USERS, updateAssignedUsers);
  yield takeLatestSafe(COPY_ROLE, copyRoleToOtherOrgs);
}

function takeLatestSafe(action, effect) {
  return takeLatest(action, tryCatch(effect));
}

function tryCatch(effect) {
  return function* (args) {
    try {
      yield call(effect, args);
    } catch (err) {
      console.error(err);
      if (err.response.status === 422) {
        yield put({ type: SET_ERROR, payload: err.response.data });
      }
    }
  };
}

function* getCurrentUserData(data) {
  yield put({ type: SET_LOADING, payload: true });

  yield put({ type: FETCH_CURRENT_USER_SUCCESS, payload: data.payload });

  yield fetchRoles();
  yield fetchPermissions();
  yield put({ type: SET_LOADING, payload: false });
}

function* getUsersPerRole() {
  const { id } = yield getCommonApiParams();

  const { data } = yield call(api.getUsersPerRole, id);

  yield put({
    type: FETCH_USERS_PER_ROLE_SUCCESS,
    payload: data
  });
}

function* updateAssignedUsers() {
  const { id, user_id, orgName } = yield getCommonApiParams();

  let {
    data: { name, users_count, tag }
  } = yield call(api.updateUsers, { id, user_id });

  yield put({
    type: EDIT_ROLE_SUCCESS,
    payload: { name, id, tag, orgName, users_count }
  });
}

function* copyRoleToOtherOrgs() {
  const { id, organization_ids, organizations, copyName } = yield getCommonApiParams();

  let { data } = yield call(api.copyRole, { id, role: { organization_ids, name: copyName } });

  yield put({
    type: COPY_ROLE_SUCCESS,
    payload: { data, organizations }
  });
}

function* fetchRoles() {
  const { data } = yield call(api.getRoles);

  yield put({
    type: FETCH_ROLES_SUCCESS,
    payload: data
  });
}

function* fetchPermissions() {
  const { data } = yield call(api.getPermissions);

  yield put({
    type: FETCH_PERMISSIONS_SUCCESS,
    payload: data
  });
}

function* addRole() {
  const { role, orgName } = yield getCommonApiParams();

  const { data } = yield call(api.addRole, { role });

  yield put({
    type: ADD_ROLE_SUCCESS,
    payload: { data, orgName }
  });
}

function* editRole({ payload }) {
  const { id, orgName } = yield getCommonApiParams();

  let { selectedPermissions: { permissions, reporting_portal_permissions }, name } = payload;

  const { data: { permissions_tag } } = yield call(api.updateRole, id, {
    role: {
      name,
      permission_ids: permissions.join(","),
      reporting_portal_ids: reporting_portal_permissions.join(",")
    }
  });

  yield put({
    type: EDIT_ROLE_SUCCESS,
    payload: { orgName, permissions, reporting_portal_permissions, name, id, permissions_tag }
  });
}

function* removeRole() {
  const { id, orgName } = yield getCommonApiParams();
  yield call(api.removeRole, id);

  yield put({
    type: DELETE_ROLE_SUCCESS,
    payload: { id: +id, orgName }
  });
}

function* getCommonApiParams() {
  return yield select((state) => {
    const {
      selected: {
        roleId: id,
        organization,
        name,
        copyName,
        identifier,
        permissions,
        selectedUsers,
        selectedOrganizations
      }
    } = state.roles;

    return {
      id: id.toString(),
      orgName: organization?.name,
      copyName,
      user_id: selectedUsers
        .filter((user) => !user.assigned)
        .map((user) => user.id)
        .join(","),
      organization_ids: selectedOrganizations.map((org) => org.id).join(","),
      organizations: selectedOrganizations,
      role: {
        organization_id: organization?.id.toString(),
        name: name.trim(),
        identifier: identifier.trim(),
        permissions
      }
    };
  });
}
